import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { clientsAPI, casesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  Plus, 
  Search, 
  Edit,
  Trash2,
  Eye,
  Users,
  Phone,
  Mail,
  MapPin,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';

const ClientsPage = () => {
  const { t, language } = useLanguage();
  const { canManageClients } = useAuth();
  const [clients, setClients] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [viewingClient, setViewingClient] = useState(null);
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    address: '',
    id_number: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, casesRes] = await Promise.all([
        clientsAPI.getAll(),
        casesAPI.getAll()
      ]);
      setClients(clientsRes.data);
      setCases(casesRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await clientsAPI.update(editingClient.id, formData);
        toast.success(language === 'ar' ? 'تم تحديث العميل' : 'Client updated');
      } else {
        await clientsAPI.create(formData);
        toast.success(language === 'ar' ? 'تم إضافة العميل' : 'Client created');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(t('error'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد؟' : 'Are you sure?')) return;
    try {
      await clientsAPI.delete(id);
      toast.success(language === 'ar' ? 'تم حذف العميل' : 'Client deleted');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(t('error'));
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: '',
      email: '',
      phone: '',
      address: '',
      id_number: '',
      notes: ''
    });
    setEditingClient(null);
  };

  const openEditDialog = (client) => {
    setEditingClient(client);
    setFormData({
      full_name: client.full_name,
      email: client.email || '',
      phone: client.phone,
      address: client.address || '',
      id_number: client.id_number || '',
      notes: client.notes || ''
    });
    setDialogOpen(true);
  };

  const getClientCases = (clientId) => {
    return cases.filter(c => c.client_id === clientId);
  };

  const filteredClients = clients.filter(c => 
    c.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.phone.includes(searchTerm) ||
    (c.email && c.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="clients-page">
      <Header title={t('clients')} />
      
      <div className="p-6 space-y-6">
        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="relative flex-1 sm:max-w-xs w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t('search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="search-clients"
            />
          </div>

          {canManageClients && (
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="btn-accent gap-2" data-testid="create-client-btn">
                  <Plus className="w-4 h-4" />
                  {t('createClient')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>
                    {editingClient ? t('editClient') : t('createClient')}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>{t('clientName')} *</Label>
                    <Input
                      value={formData.full_name}
                      onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                      required
                      data-testid="client-name-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('phone')} *</Label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        required
                        data-testid="client-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('email')}</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        data-testid="client-email-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('idNumber')}</Label>
                    <Input
                      value={formData.id_number}
                      onChange={(e) => setFormData({...formData, id_number: e.target.value})}
                      data-testid="client-id-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('address')}</Label>
                    <Input
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      data-testid="client-address-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('notes')}</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      rows={2}
                      data-testid="client-notes-input"
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      {t('cancel')}
                    </Button>
                    <Button type="submit" className="btn-accent" data-testid="client-submit-btn">
                      {t('save')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Clients Grid */}
        {filteredClients.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredClients.map((client) => (
              <Card 
                key={client.id} 
                className="hover:shadow-md transition-shadow"
                data-testid={`client-card-${client.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                        <Users className="w-5 h-5 text-blue-500" strokeWidth={1.5} />
                      </div>
                      <div>
                        <CardTitle className="text-base">{client.full_name}</CardTitle>
                        {client.id_number && (
                          <p className="text-xs text-muted-foreground font-mono">{client.id_number}</p>
                        )}
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      <Briefcase className="w-3 h-3 mr-1" />
                      {getClientCases(client.id).length}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="w-4 h-4" strokeWidth={1.5} />
                      <span>{client.phone}</span>
                    </div>
                    {client.email && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Mail className="w-4 h-4" strokeWidth={1.5} />
                        <span className="truncate">{client.email}</span>
                      </div>
                    )}
                    {client.address && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="w-4 h-4" strokeWidth={1.5} />
                        <span className="truncate">{client.address}</span>
                      </div>
                    )}
                  </div>
                  
                  {canManageClients && (
                    <div className="flex items-center gap-2 pt-3 border-t border-border">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => setViewingClient(client)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        {t('view')}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => openEditDialog(client)}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        {t('edit')}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDelete(client.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" strokeWidth={1} />
            <p className="text-muted-foreground">{t('noData')}</p>
          </Card>
        )}

        {/* View Client Dialog */}
        <Dialog open={!!viewingClient} onOpenChange={() => setViewingClient(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('clientDetails')}</DialogTitle>
            </DialogHeader>
            {viewingClient && (
              <div className="space-y-4 mt-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <Users className="w-8 h-8 text-blue-500" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold">{viewingClient.full_name}</h3>
                    {viewingClient.id_number && (
                      <p className="text-sm text-muted-foreground font-mono">{viewingClient.id_number}</p>
                    )}
                  </div>
                </div>
                
                <div className="space-y-3 pt-4 border-t">
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                    <span>{viewingClient.phone}</span>
                  </div>
                  {viewingClient.email && (
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                      <span>{viewingClient.email}</span>
                    </div>
                  )}
                  {viewingClient.address && (
                    <div className="flex items-center gap-3">
                      <MapPin className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                      <span>{viewingClient.address}</span>
                    </div>
                  )}
                </div>

                {viewingClient.notes && (
                  <div className="pt-4 border-t">
                    <p className="text-sm text-muted-foreground mb-1">{t('notes')}</p>
                    <p>{viewingClient.notes}</p>
                  </div>
                )}

                {/* Linked Cases */}
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-3">{t('linkedCases')}</h4>
                  {getClientCases(viewingClient.id).length > 0 ? (
                    <div className="space-y-2">
                      {getClientCases(viewingClient.id).map(c => (
                        <div key={c.id} className="flex items-center justify-between p-2 rounded bg-muted/50">
                          <div>
                            <p className="font-medium text-sm">{c.title}</p>
                            <p className="text-xs text-muted-foreground">{c.case_number}</p>
                          </div>
                          <Badge className={`status-${c.status}`}>{c.status}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">{t('noData')}</p>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ClientsPage;
